import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded'
import RepeatRoundedIcon from '@mui/icons-material/RepeatRounded'
import { IconButton, Tooltip, Typography } from '@mui/material'
import { AppBox } from 'src/component/common/analog/molecule/AppBox'
import { ColorTokens } from 'src/feature/theme/model/Palette'

export type AssistantHeaderProps = {
  title: string;
  subtitle: string;
  switchTopicLabel: string;
  skillsTopicLabel: string;
  platformTopicLabel: string;
  isPlatformTopic: boolean;
  clearChatLabel: string;
  canClearChat: boolean;
  onSwitchTopic: () => void;
  onClearChat: () => void;
  onClose: () => void;
};

export function AssistantHeader({
  title,
  subtitle,
  switchTopicLabel,
  skillsTopicLabel,
  platformTopicLabel,
  isPlatformTopic,
  clearChatLabel,
  canClearChat,
  onSwitchTopic,
  onClearChat,
  onClose,
}: AssistantHeaderProps) {
  return (
    <AppBox sx={styles.header}>
      <AppBox sx={styles.headerLeft}>
        <AppBox sx={styles.headerIcon}>
          <AutoAwesomeRoundedIcon
            sx={{ fontSize: 16, color: ColorTokens.primary500 }}
          />
        </AppBox>
        <AppBox sx={{ flexDirection: 'column' }}>
          <AppBox sx={styles.titleRow}>
            <Typography variant="h6" sx={styles.headerTitle}>
              {title}
            </Typography>
            <Tooltip title={switchTopicLabel} placement="top">
              <AppBox
                onClick={onSwitchTopic}
                sx={styles.switchTopicButton}
              >
                <Typography
                  component="span"
                  sx={isPlatformTopic ? styles.inactiveTopic : styles.activeTopic}
                >
                  {skillsTopicLabel}
                </Typography>
                <RepeatRoundedIcon sx={styles.switchTopicIcon} />
                <Typography
                  component="span"
                  sx={isPlatformTopic ? styles.activeTopic : styles.inactiveTopic}
                >
                  {platformTopicLabel}
                </Typography>
              </AppBox>
            </Tooltip>
          </AppBox>
          <Typography sx={styles.headerSubtitle}>{subtitle}</Typography>
        </AppBox>
      </AppBox>
      <AppBox sx={{ flexDirection: 'row', gap: 4 }}>
        {canClearChat && (
          <Tooltip title={clearChatLabel} placement="top">
            <IconButton
              size="small"
              onClick={onClearChat}
              sx={{
                ...styles.headerAction,
                backgroundColor: ColorTokens.red10,
              }}
            >
              <DeleteOutlineRoundedIcon
                sx={{ fontSize: 16, color: 'error.main' }}
              />
            </IconButton>
          </Tooltip>
        )}
        <IconButton onClick={onClose} sx={styles.headerAction}>
          <CloseRoundedIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </AppBox>
    </AppBox>
  )
}

const styles = {
  header: {
    width: '100%',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 15px',
    backgroundColor: 'background.dark',
    borderBottom: '1px solid rgba(0,0,0,0.07)',
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  headerIcon: {
    width: 35,
    height: 35,
    borderRadius: '50%',
    backgroundColor: 'background.paper',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  headerTitle: {
    fontWeight: 600,
    lineHeight: 1.2,
    color: ColorTokens.primary500,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
  },
  switchTopicButton: {
    alignItems: 'center',
    gap: '5px',
    minWidth: 0,
    padding: '3px 6px',
    whiteSpace: 'nowrap',
    backgroundColor: 'background.paper',
    borderRadius: '999px',
    border: '1px solid',
    borderColor: ColorTokens.border100,
    cursor: 'pointer',
  },
  switchTopicIcon: {
    fontSize: '12px !important',
    color: ColorTokens.primary500,
  },
  activeTopic: {
    fontSize: 11,
    fontWeight: 700,
    lineHeight: 1,
    color: ColorTokens.primary500,
  },
  inactiveTopic: {
    fontSize: 11,
    fontWeight: 400,
    lineHeight: 1,
    color: 'text.secondary',
  },
  headerSubtitle: {
    fontSize: 11,
    color: 'text.secondary',
  },
  headerAction: {
    width: 28,
    height: 28,
    color: 'text.secondary',
  },
}
